// A generic drill-down box plot: one horizontal box (min / Q1 / median / Q3 / max) per row, rows
// sorted by median, 20 per page, a click drills one navigation level down through the app's
// context updaters. Schema-agnostic by rule: no project SQL, no table names, no store —
// everything domain-specific arrives through the function props below, exactly like the
// stacked bar's (PLATFORM.md §3.2 rules 1–3). Born at IA_ABSORPTION_PLAN Phase 4 step 12d from
// issue-analysis's own `charts/base/BoxPlot.vue`.
//
//   props:  title            (String)        rendered as-is; already translated
//           chartCardHeight  (Number)        the card's max height; the chart sizes itself to
//                                            the row count and the card scrolls
//           dataProviders    (Object)        { [level]: () => Promise<Row[]> }
//           contextUpdaters  (Object)        { [level]: (title, parentId) => Promise<void>, reset }
//           navigationConfig (Array)         ordered [{ level, titleKey }] — titleKey RAW
//           filterContext    (Object|Array)  the filter-context duck type, or an array of them
//                                            (reload on a tick from any)
//           dataLoaded       (Boolean)       gates the first load
//           skipEmptyLevels  (Boolean)       the top is the first level whose provider has rows
//           navigation       (String)        'breadcrumbs' (default) | 'arrows' (one level up /
//                                            down buttons instead of the breadcrumb row)
//           color            (String)        the box fill; empty = the theme's boxPlot.box
//           reloadKey        (any)           a change reloads like a filter tick (the app's own
//                                            parameters, e.g. the selected status)
//           exportFilename   (String)        opt-in: a download button at the end of the title row
//                                            that saves the chart as a PNG ('' = no button)
//           exportTitle      (String)        the PNG's title; '' = `title`
//   slots:  controls                         the page's header controls, after the title
//   emits:  none — filtering happens by calling `contextUpdaters`, never by emitting
//   setup:  downloadPng()  the button's action, callable through a template ref (no-op without
//                          `exportFilename`)
//
// Row: { title, parentId?, count, min, Q1, median, Q3, max, average, standard_deviation } —
// values are DURATIONS IN DAYS (axis + tooltip labels are formatTimeValue's).
// There is deliberately NO `chartFontColor` prop: every colour comes from `useChartTheme()`.
//
// Full contract: ../../../README.md. The logic lives in ./setup.js.
//
// The echarts module registration is wiring for the `VChart` registration below — both are
// what the template's `<v-chart>` needs — so it lives here and ./setup.js stays pure logic.
// The old SFC also registered ScatterChart/LegendComponent/TitleComponent/DataZoomComponent,
// which nothing in its option used; they are dropped.
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { BoxplotChart } from "echarts/charts";
import { TooltipComponent, GridComponent } from "echarts/components";
import VChart from "vue-echarts";
import setup from './setup.js';

use([CanvasRenderer, BoxplotChart, TooltipComponent, GridComponent]);

export default {
  name: 'base-box-plot-chart',
  props: {
    // Rendered as-is — already translated by the page
    title: {
      type: String,
      default: ''
    },
    // The card's max height (it scrolls beyond it) and the chart's height before the first rows
    chartCardHeight: {
      type: Number,
      default: 460
    },
    // { [level]: () => Promise<Row[]> }
    dataProviders: {
      type: Object,
      required: true
    },
    // { [level]: (title, parentId) => Promise<void>, reset: () => Promise<void> }
    contextUpdaters: {
      type: Object,
      required: true
    },
    // Ordered [{ level, titleKey }]; titleKey is a RAW app key the base translates
    navigationConfig: {
      type: Array,
      required: true
    },
    // One filter-context duck type, or an ARRAY of them: the chart reloads on a tick from any
    filterContext: {
      type: [Object, Array],
      required: true
    },
    dataLoaded: {
      type: Boolean,
      default: false
    },
    // The top of the chart is the first navigation level whose data provider returns rows,
    // re-resolved on every reload at the top (the stacked bar's opt-in of the same name)
    skipEmptyLevels: {
      type: Boolean,
      default: false
    },
    // 'breadcrumbs': the drill path as a breadcrumb row (click a crumb to go back);
    // 'arrows': one-level-up / one-level-down buttons (down shows the next level unfiltered)
    navigation: {
      type: String,
      default: 'breadcrumbs',
      validator: (v) => ['breadcrumbs', 'arrows'].includes(v)
    },
    // The box fill (the border is this darkened by 20); empty = the theme's boxPlot.box
    color: {
      type: String,
      default: null
    },
    // Any value: a change reloads the chart as a filter tick does (at the top the top level is
    // re-resolved, drilled in the current level reloads). For app state the providers read.
    reloadKey: {
      type: null,
      default: null
    },
    // Opt-in (2026-10-16, PNG export on every chart): a download button at the end of the title
    // row that saves the chart through @rdrive/helper-chart-export — no legend, the card title as
    // the PNG's title and, with navigation="breadcrumbs" once drilled, the trail as its subtitle.
    // '' = no button, the title row exactly as before.
    exportFilename: {
      type: String,
      default: ''
    },
    // The PNG's title; '' = `title`
    exportTitle: {
      type: String,
      default: ''
    }
  },
  emits: [],
  // `<v-chart>` must be registered explicitly in a classic component, or Vue logs "Failed to
  // resolve component: v-chart" and renders nothing. Vuetify tags need no entry —
  // vite-plugin-vuetify rewrites the compiled render code, not the script block.
  components: { VChart },
  setup,
};
